import React from 'react';

/**
 * Top-level error boundary. Without one, an uncaught render error anywhere
 * in the tree aborts the entire React commit and leaves nothing in #root —
 * the user sees exactly the raw index.html background color with no content
 * and no explanation (see index.html's `background-color: #0A1812` on
 * html/body, which is what's still visible in that failure mode). That's
 * indistinguishable from "the page never loaded" and gives a support
 * conversation nothing to go on.
 *
 * Deliberately class-based — this is the one place in the app that still
 * needs to be, since getDerivedStateFromError/componentDidCatch have no
 * hook equivalent.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[MarketNerve] Uncaught render error:', error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: '1.25rem',
        padding: '2rem 1.5rem', textAlign: 'center',
        background: '#0A1812', color: '#F0EBD8',
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        <div style={{ fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C49A38', fontWeight: 700 }}>
          Something went wrong
        </div>
        <p style={{ maxWidth: '360px', fontSize: '0.85rem', lineHeight: 1.6, color: 'rgba(240,235,216,0.7)', margin: 0 }}>
          MarketNerve hit an unexpected error loading this page. Reloading usually fixes it 
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: '#C49A38', color: '#07100D', border: 'none', borderRadius: '6px',
            padding: '0.7rem 1.6rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
